import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import './AdminPanel.css';

const ADMIN_EMAILS = ['junlim3@illinois.edu', 'alexison@usc.edu', 'alexison.pan@gmail.com'];

const AdminPanel = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const navigate = useNavigate();

  // Fetch allowed users
  const fetchAllowedUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'allowedUsers'));
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllowedUsers(users);
    } catch (error) {
      console.error('Error fetching allowed users:', error);
    }
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      console.log('Current auth state:', {
        user: user ? {
          email: user.email,
          uid: user.uid,
          emailVerified: user.emailVerified,
        } : null,
        isAdmin: user ? ADMIN_EMAILS.includes(user.email) : false
      });
      
      if (!user) {
        console.log('No user logged in');
        navigate('/');
        return;
      }

      if (!ADMIN_EMAILS.includes(user.email)) {
        console.log('User is not an admin');
        navigate('/');
        return;
      }

      // Test Firestore connection with more permissive rules
      try {
        const testRef = collection(db, 'allowedUsers');
        console.log('Attempting to read from Firestore...');
        const testQuery = await getDocs(testRef);
        console.log('Firestore read successful:', {
          exists: !testQuery.empty,
          count: testQuery.size
        });
      } catch (error) {
        console.error('Firestore connection error:', {
          code: error.code,
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }

      await fetchAllowedUsers();
    };

    // Run check immediately and on auth state change
    checkAdminStatus();
    const unsubscribe = auth.onAuthStateChanged(checkAdminStatus);
    return () => unsubscribe();
  }, [navigate]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    if (!email) {
      setMessage('Please enter an email address');
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      const currentUser = auth.currentUser;
      console.log('Current user:', currentUser?.email);
      console.log('Is admin?', ADMIN_EMAILS.includes(currentUser?.email));
      
      if (!currentUser || !ADMIN_EMAILS.includes(currentUser.email)) {
        throw new Error('Unauthorized access');
      }

      console.log('Attempting to add user:', email);

      const docRef = await addDoc(collection(db, 'allowedUsers'), {
        email: email.toLowerCase(),
        dateAdded: new Date().toISOString(),
        addedBy: currentUser.email
      });

      console.log('Successfully added document with ID:', docRef.id);
      
      setMessage(`Successfully added ${email} to allowed users`);
      setMessageType('success');
      setEmail('');
    } catch (error) {
      console.error('Detailed error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Error adding user. Please try again.';
      if (error.message === 'Unauthorized access') {
        errorMessage = 'You are not authorized to add users';
      } else if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your admin status.';
      }
      
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      <h2>Admin Panel</h2>
      <div className="admin-section">
        <h3>Add Authorized User</h3>
        <form onSubmit={handleAddUser}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email to authorize"
            className="email-input"
            disabled={loading}
          />
          <button 
            type="submit" 
            className="add-user-btn"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add User'}
          </button>
        </form>
        {message && (
          <p className={`message ${messageType}`}>
            {message}
          </p>
        )}
      </div>

      <div className="admin-section">
        <h3>Authorized Users</h3>
        <div className="users-list">
          {allowedUsers.map(user => (
            <div key={user.id} className="user-item">
              <span className="user-email">{user.email}</span>
              <span className="user-added">
                Added by: {user.addedBy}
                <br />
                on {new Date(user.dateAdded).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 